import axios from "axios";

import {
  isTokenExpired,
  redirectToLogin as utilsRedirectToLogin,
  getAuthToken,
  isAuthenticated,
} from "@/utils/authUtils";

// Define custom error interface - kept for potential future use
// interface ApiError extends Error {
//   isNetworkError?: boolean;
//   isApiStructureError?: boolean;
//   message: string;
//   response?: {
//     status: number;
//     data: any;
//     headers: any;
//   };
//   request?: any;
// }

// Potential API URLs to try
const API_URLS = [
  import.meta.env.VITE_API_URL,
  "http://localhost:8000",
  "http://127.0.0.1:8000",
].filter(Boolean);

// Use only the first valid URL
const API_URL = API_URLS[0] || "http://localhost:8000";

// DISABLE ALL AUTOMATIC API CALLS
const DISABLE_AUTO_API_CALLS = true;

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: 15000, // 15 seconds
});

// Simple request interceptor that just adds auth token
api.interceptors.request.use(
  (config) => {
    // Block any requests that aren't user-initiated
    if (config.headers?.["X-Auto-Check"] === "true" && DISABLE_AUTO_API_CALLS) {
      // Create a fake canceled request
      return Promise.reject({
        __CANCELED__: true,
        message: "Automatic API calls are disabled",
      });
    }

    // Add auth token
    const token = getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add client ID from localStorage
    const clientId = localStorage.getItem("clientId");

    if (clientId) {
      if (!config.params) config.params = {};
      config.params.clientId = clientId;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Simple response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle canceled requests
    if (error.__CANCELED__) {
      return Promise.reject(new Error("API call was blocked"));
    }

    // Handle 401 Unauthorized
    if (
      error.response?.status === 401 &&
      !error.config?.headers?.["X-Auto-Check"]
    ) {
      // Only redirect for user-initiated requests
      utilsRedirectToLogin();
    }

    return Promise.reject(error);
  },
);

// Helper function for endpoints - kept for potential future use
// const getEndpoint = (path: string): string => {
//   const cleanPath = path.startsWith("/") ? path.substring(1) : path;
//
//   if (cleanPath.startsWith("auth/") || cleanPath.startsWith("api/auth/")) {
//     return cleanPath.startsWith("api/") ? cleanPath : `api/${cleanPath}`;
//   }
//
//   return cleanPath.startsWith("api/") ? cleanPath : `api/${cleanPath}`;
// };

// New exportable function to check authentication status
export const checkAuthentication = async () => {
  const token = getAuthToken();
  const localAuthStatus = isAuthenticated();

  if (!token) {
    return { authenticated: false, message: "No token found" };
  }

  if (!localAuthStatus) {
    return { authenticated: false, message: "Token expired" };
  }

  try {
    // Optional: Make a lightweight API call to verify token on server
    const response = await api
      .get("/api/auth/verify", {
        headers: { "X-Auto-Check": "false" },
      })
      .catch((error) => {
        // If server verification fails, still trust the local validation
        if (error.response?.status === 401) {
          return { data: { authenticated: false } };
        }

        return { data: { authenticated: localAuthStatus } };
      });

    return {
      authenticated: response.data?.authenticated ?? localAuthStatus,
      user: response.data?.user || null,
    };
    } catch (error) {
      // If server is unreachable, fall back to local validation
      return {
        authenticated: localAuthStatus,
        message: "Using local validation",
      };
    }
};

// Authentication API
export const authAPI = {
  login: async (email: string, password: string) => {
    try {
      const loginUrl = `${API_URL}/api/auth/login`;

      const loginResponse = await axios.post(
        loginUrl,
        { email, password },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          timeout: 15000,
        },
      );

      if (loginResponse.data.token) {
        localStorage.setItem("token", loginResponse.data.token);

        if (loginResponse.data.user) {
          localStorage.setItem(
            "userId",
            loginResponse.data.user.id || loginResponse.data.user.userId,
          );
          localStorage.setItem("role", loginResponse.data.user.role);

          const clientId =
            loginResponse.data.user.client_id ||
            loginResponse.data.user.clientId;

          if (clientId) {
            localStorage.setItem("clientId", clientId);
          }

          const clientName =
            loginResponse.data.user.client_name ||
            loginResponse.data.user.clientName;

          if (clientName) {
            localStorage.setItem("clientName", clientName);
          }
        }
      }

      return loginResponse.data;
    } catch (error) {
      throw error;
    }
  },

  register: async (userData: any) => {
    try {
      const registerUrl = `${API_URL}/api/auth/register`;

      const response = await axios.post(registerUrl, userData, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        timeout: 15000,
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  logout: async () => {
    try {
      const token = localStorage.getItem("token");

      if (token) {
        await axios.post(
          `${API_URL}/api/auth/logout`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          },
        );
      }
    } catch (error) {
      // Silently handle logout errors
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("userId");
      localStorage.removeItem("role");
      localStorage.removeItem("clientId");
      localStorage.removeItem("clientName");
    }
  },

  isAuthenticated: () => {
    const token = localStorage.getItem("token");

    return !!token && !isTokenExpired(token);
  },
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    try {
      const response = await api.get("/api/dashboard/stats", {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return (
        response.data || {
          totalBooks: 0,
          totalInventoryValue: 0,
          totalSales: 0,
          recentOrders: [],
          topSellingBooks: [],
          orderStatusStats: {
            pending: 0,
            completed: 0,
            cancelled: 0,
            added: 0,
          },
        }
      );
    } catch (error) {
      return {
        totalBooks: 0,
        totalInventoryValue: 0,
        totalSales: 0,
        recentOrders: [],
        topSellingBooks: [],
        orderStatusStats: {
          pending: 0,
          completed: 0,
          cancelled: 0,
          added: 0,
        },
      };
    }
  },
};

// Clients API
export const clientsAPI = {
  getAll: async () => {
    try {
      const response = await api.get("/api/clients", {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      return [];
    }
  },

  getById: async (id: string | number) => {
    try {
      const response = await api.get(`/api/clients/${id}`, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      return null;
    }
  },

  create: async (clientData: any) => {
    try {
      const response = await api.post("/api/clients", clientData, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (id: string | number) => {
    try {
      const response = await api.delete(`/api/clients/${id}`, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Books API
export const booksAPI = {
  getAll: async (
    clientId?: string | number,
    options?: { includeVersions?: boolean },
  ) => {
    try {
      const params = {
        ...(clientId ? { clientId } : {}),
        ...(options?.includeVersions ? { include_versions: 1 } : {}),
      };
      const response = await api.get("/api/books", {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
        params,
      });

      return response.data;
    } catch (error) {
      return [];
    }
  },
  getById: async (id: string | number) => {
    try {
      const response = await api.get(`/api/books/${id}`, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      return null;
    }
  },
  create: async (bookData: any) => {
    try {
      const response = await api.post("/api/books", bookData, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },
  update: async (id: string | number, bookData: any) => {
    try {
      const response = await api.put(`/api/books/${id}`, bookData, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },
  delete: async (id: string | number) => {
    try {
      // Get clientId from localStorage
      const clientId = localStorage.getItem("clientId");
      
      // Check if clientId is available
      if (!clientId) {
        throw new Error("Client ID is required for book deletion");
      }
      
      const response = await api.delete(`/api/books/${id}`, {
        headers: { 
          "X-Auto-Check": "false", // Mark as user-initiated
          "Content-Type": "application/json", 
          "Accept": "application/json"
        },
        params: { clientId } // Add clientId as a query parameter
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Sales API
export const salesAPI = {
  getAll: async (clientId?: string | number) => {
    try {
      const params = clientId ? { clientId } : {};
      const response = await api.get("/api/sales", {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
        params,
      });

      return response.data;
    } catch (error) {
      return [];
    }
  },
  getById: async (id: string | number) => {
    try {
      const response = await api.get(`/api/sales/${id}`, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      return null;
    }
  },
  create: async (saleData: any) => {
    try {
      const response = await api.post("/api/sales", saleData, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },
  update: async (id: string | number, saleData: any) => {
    try {
      const response = await api.put(`/api/sales/${id}`, saleData, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },
  delete: async (id: string | number) => {
    try {
      const response = await api.delete(`/api/sales/${id}`, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },
  // Get sales by date range and optional book ID
  getByDateRange: async (startDate: string, endDate: string, bookId?: string): Promise<any[]> => {
    try {
      let url = `${API_URL}/sales/by-date?start_date=${startDate}&end_date=${endDate}`;
      
      if (bookId) {
        url += `&book_id=${bookId}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      return response.data.data || [];
    } catch (error) {
      return [];
    }
  },
  
  // Generate invoice from the backend
  generateInvoice: async (id: string | number, clientId?: string | number) => {
    try {
      // Use the web route which will be handled by our middleware
      return `${API_URL}/sales/${id}/invoice?client_id=${clientId || ''}`;
    } catch (error) {
      throw error;
    }
  }
};

// Inventory API
export const inventoryAPI = {
  getAll: async (clientId?: string | number) => {
    try {
      const params = clientId ? { clientId } : {};
      const response = await api.get("/api/inventory", {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
        params,
      });

      return response.data;
    } catch (error) {
      return [];
    }
  },

  getById: async (id: string | number) => {
    try {
      const response = await api.get(`/api/inventory/${id}`, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      return null;
    }
  },

  create: async (inventoryData: any) => {
    try {
      const response = await api.post("/api/inventory", inventoryData, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (id: string | number, inventoryData: any) => {
    try {
      const response = await api.put(`/api/inventory/${id}`, inventoryData, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (id: string | number) => {
    try {
      const response = await api.delete(`/api/inventory/${id}`, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },
  // Get inventory by date range and optional book ID
  getByDateRange: async (startDate: string, endDate: string, bookId?: string): Promise<any[]> => {
    try {
      let url = `${API_URL}/inventory/by-date?start_date=${startDate}&end_date=${endDate}`;
      
      if (bookId) {
        url += `&book_id=${bookId}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      return response.data.data || [];
    } catch (error) {
      return [];
    }
  },
};

// Orders API
export const ordersAPI = {
  getAll: async () => {
    try {
      const response = await api.get("/api/orders", {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      return [];
    }
  },

  create: async (orderData: any) => {
    try {
      const response = await api.post("/api/orders", orderData, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (id: string | number, orderData: any) => {
    try {
      const response = await api.put(`/api/orders/${id}`, orderData, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  complete: async (id: string | number, orderData: any) => {
    try {
      // Add status=completed to the order data
      const completeData = {
        ...orderData,
        status: "completed"
      };
      
      const response = await api.put(`/api/orders/${id}/complete`, completeData, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  cancel: async (id: string | number) => {
    try {
      const response = await api.put(`/api/orders/${id}/cancel`, 
        { status: "cancelled" },
        { headers: { "X-Auto-Check": "false" } } // Mark as user-initiated
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Users API
export const usersAPI = {
  getAll: async () => {
    try {
      const response = await api.get("/api/users", {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      return [];
    }
  },

  getById: async (id: string | number) => {
    try {
      const response = await api.get(`/api/users/${id}`, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      return null;
    }
  },

  create: async (userData: any) => {
    try {
      const response = await api.post("/api/users", userData, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (id: string | number, userData: any) => {
    try {
      const response = await api.put(`/api/users/${id}`, userData, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (id: string | number) => {
    try {
      const response = await api.delete(`/api/users/${id}`, {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  checkEmail: async (email: string, clientId?: string | number) => {
    try {
      // Change from POST to GET method, with email in query params
      // Include the clientId if provided
      const params: any = { email };

      if (clientId) params.clientId = clientId;

      const response = await api.get("/api/users/check-email", {
        headers: { "X-Auto-Check": "false" }, // Mark as user-initiated
        params,
      });

      return response.data;
    } catch (error) {
      // Default to returning that email doesn't exist on error
      return { exists: false };
    }
  },
};

// Export the API instance
export default api;
